import { FocusKeyManager, FocusOrigin } from '@angular/cdk/a11y';
import { DOWN_ARROW, ESCAPE, UP_ARROW } from '@angular/cdk/keycodes';
import { AfterViewInit, Component, ContentChildren, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, TemplateRef, ViewChild } from '@angular/core';
import { MenuItemComponent } from '../menu-item/menu-item.component';

let nextUniqueId = 0;

/**
 * Adds a menu.
 *
 * The menu consists of a trigger and the menu content.
 *
 * The trigger can be any element (a button is preferred because of its native capabilities).
 * To act as a menu, the directive `appMenuTrigger` must be added.
 * Also, it must point to the template reference of the menu content (using the `[menuTriggerFor]` binding).
 *
 * The menu content is an `<app-menu>` element which needs to have the template reference used by the menu trigger.
 * The `app-menu` component also accepts a `title` binding to show a title above the items when the mobile menu is shown.
 * 
 * The menu items can be any element (it should be focusable, a button or anchor is preferred).
 * Each "real" item needs to have the `app-menu-item` directive/component.
 * Apart from that, the usage of the button/anchor is the same as normal.
 * Each item can be disabled using the default disabled attribute.
 *
 * A `app-menu-divider` can be used to divide the items.
 *
 * Example:
 *
 * ```html
 * <button appMenuTrigger [menuTriggerFor]="myMenu" type="button">Menu trigger</button>
 * <app-menu [title]="'Optional title for mobile view'" #myMenu>
 *   <button app-menu-item (click)="doSomething()">Item 1</button>
 *   <a app-menu-item [routerLink]="['/foo']">Item 1</a>
 *   <app-menu-divider></app-menu-divider>
 *   <a app-menu-item href="https://salfeld.de">Homepage</a>
 * <app-menu>
 * ```
 */
@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit, OnDestroy, AfterViewInit {
  private keyManager: FocusKeyManager<MenuItemComponent>;

  public readonly uniqueId = nextUniqueId++;
  public readonly menuId = `menu-${this.uniqueId}`;

  /** Used to keep the menu at least as wide as the trigger */
  public minWidth: number|null = null;

  @ViewChild('menuPortal')
  public menuPortalRef: TemplateRef<unknown>;

  @ContentChildren(MenuItemComponent, { descendants: true })
  public items: QueryList<MenuItemComponent>;

  /** Title of the menu which is shown in mobile view (optional) */
  @Input()
  public title?: string;

  @Output()
  public closed = new EventEmitter<MenuCloseReason>();

  constructor() { }

  ngOnInit(): void {
  }

  ngOnDestroy() {
    this.closed.complete();
  }

  public ngAfterViewInit() {
    this.keyManager = new FocusKeyManager(this.items).withWrap().withTypeAhead().withHomeAndEnd();

    this.keyManager.tabOut.subscribe(() => {
      this.closed.emit('tab');
    });
  }

  public focusFirstItem(origin: FocusOrigin) {
    this.keyManager.setFocusOrigin(origin).setFirstItemActive();
  }

  public onKeydown(event: KeyboardEvent) {
    if (event.keyCode === ESCAPE) {
      this.closed.emit('keydown');
    } else {
      if (event.keyCode === UP_ARROW || event.keyCode === DOWN_ARROW) {
        this.keyManager.setFocusOrigin('keyboard');
      }

      this.keyManager.onKeydown(event);
    }
  }

  public onMenuClick() {
    this.closed.emit('click');
  }
}

export type MenuCloseReason = void | 'click' | 'keydown' | 'tab';
